import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ContentPage, waLink } from "@/components/site/ContentPage";
import { pageHead, SITE_URL } from "@/lib/seo";
import portrait2 from "@/assets/kk-portrait-2.webp";

const TITLE = "Dr. Kelson Kawamura | Cirurgião plástico em Foz";
const DESCRIPTION =
  "Conheça a formação, a experiência e a filosofia de atendimento do Dr. Kelson Kawamura, cirurgião plástico em Foz do Iguaçu.";
const PATH = "/dr-kelson-kawamura";

export const Route = createFileRoute("/dr-kelson-kawamura")({
  head: () => pageHead({ title: TITLE, description: DESCRIPTION, path: PATH, type: "profile" }),
  component: DrKelsonPage,
});

const FAQ = [
  {
    q: "Onde o Dr. Kelson Kawamura atende?",
    a: "O consultório está localizado no centro de Foz do Iguaçu, na Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, sala 86.",
  },
  {
    q: "Quais procedimentos o Dr. Kelson realiza?",
    a: "O atendimento prioriza mommy makeover, abdominoplastia, lipoescultura, cirurgias das mamas e rinoplastia.",
  },
  {
    q: "O Dr. Kelson é membro da SBCP?",
    a: "Sim, é **membro da Sociedade Brasileira de Cirurgia Plástica**.",
  },
  {
    q: "Como agendar uma avaliação?",
    a: "O contato pode ser feito pelo WhatsApp ou pelo telefone disponibilizado no site.",
  },
  {
    q: "A consulta já define qual cirurgia será feita?",
    a: "A consulta permite avaliar possibilidades. Exames, condições clínicas e reflexões do paciente podem ser necessários antes de uma decisão final.",
  },
];

function DrKelsonPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: "Dr. Kelson Kawamura",
    medicalSpecialty: "PlasticSurgery",
    url: `${SITE_URL}${PATH}`,
    telephone: "+55 45 99917-5813",
    email: "drkelsonkawamura@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, sala 86",
      addressLocality: "Foz do Iguaçu",
      addressRegion: "PR",
      postalCode: "85851-020",
      addressCountry: "BR",
    },
    alumniOf: [
      { "@type": "CollegeOrUniversity", name: "Universidade do Oeste de Santa Catarina (UNOESC)" },
      { "@type": "CollegeOrUniversity", name: "Hospital das Clínicas da Universidade Federal de Pernambuco" },
    ],
    memberOf: { "@type": "Organization", name: "Sociedade Brasileira de Cirurgia Plástica" },
  };

  return (
    <SiteShell>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <ContentPage
        hideSideNav
        eyebrow="Autoridade médica"
        h1="Dr. Kelson Kawamura - cirurgião plástico"
        heroTitle="Técnica, escuta e planejamento para decisões conscientes"
        lead="O Dr. Kelson Kawamura atua em cirurgia plástica com atendimento individualizado, escuta atenta e planejamento cirúrgico orientado por segurança, proporções naturais e expectativas realistas."
        image={portrait2}
        imageAlt="Dr. Kelson Kawamura, cirurgião plástico em Foz do Iguaçu"
        crumbs={[{ label: "Dr. Kelson Kawamura" }]}
        ctaLabel="Agendar avaliação"
        ctaHref={waLink("Olá! Gostaria de agendar uma avaliação com o Dr. Kelson Kawamura.")}
        ctaSecondary={{ label: "Conhecer os procedimentos", href: "/especialidades" }}
        blocks={[
          {
            h: "Uma abordagem centrada em cada paciente",
            p: [
              "Mais do que indicar uma cirurgia, o atendimento começa pela compreensão da queixa, do histórico e do momento de vida. Cada plano é construído de forma personalizada, com conversa transparente sobre possibilidades, limites, cicatrizes, recuperação e riscos.",
              "A proposta é valorizar a beleza de forma equilibrada, sem fórmulas prontas e sem padronizar corpos ou rostos.",
            ],
          },
          {
            h: "Formação e trajetória",
            list: [
              "Graduação em Medicina pela Universidade do Oeste de Santa Catarina (UNOESC).",
              "Residência em Cirurgia Plástica no Hospital das Clínicas da Universidade Federal de Pernambuco (HC-UFPE).",
              "Membro da Sociedade Brasileira de Cirurgia Plástica (SBCP).",
              "Participante do Dallas Rhinoplasty Meeting, em Dallas, Texas.",
            ],
          },
          {
            h: "Experiência profissional",
            p: [
              "São 8 anos de experiência e mais de 4 mil procedimentos realizados, com atuação concentrada em cirurgia plástica do corpo, das mamas e da face.",
            ],
          },
          {
            h: "Principais áreas de atuação",
            list: [
              "Mommy makeover.",
              "Abdominoplastia.",
              "Lipoescultura.",
              "Mamoplastia de aumento.",
              "Mastopexia com implantes.",
              "Mamoplastia redutora.",
              "Rinoplastia.",
            ],
          },
          {
            h: "Como funciona a relação médico-paciente",
            p: [
              "A consulta é conduzida como um processo de decisão compartilhada. O paciente apresenta objetivos e dúvidas; o médico avalia o que é possível, o que não é recomendado e quais cuidados serão necessários. O plano só avança quando existe entendimento claro sobre a cirurgia e o **pós-operatório**.",
            ],
          },
          {
            h: "Credenciais",
            list: ["CRM-PR 32174.", "RQE 24787.", "Membro da Sociedade Brasileira de Cirurgia Plástica."],
          },
        ]}
        related={[
          { label: "Cirurgias plásticas em Foz do Iguaçu", to: "/especialidades" },
          { label: "Primeira consulta", to: "/primeira-consulta-cirurgia-plastica" },
          { label: "Consultório", to: "/consultorio-cirurgia-plastica-foz-do-iguacu" },
        ]}
        relatedTitle="Conteúdos relacionados"
        faq={FAQ}
      />
    </SiteShell>
  );
}
